import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import AdminSlideDrawer from '@sharedCR/common/AdminSlideDrawer';
import {
  AutocompleteCDS,
  DataFieldCDS,
  RadioGroupCDS,
  TextFieldCDS,
} from '@sharedCR/coreDesignSystem/form';
import {
  CONTACT_TYPE_OPTIONS,
  ContactType,
  LIFECYCLE_STAGE_OPTIONS,
  PREFERRED_CONTACT_METHOD_OPTIONS,
} from 'app/pages/crm/shared/crm.constants';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import useForm from 'hooks/useForm';
import { getConditionalObject, trimBodyStringValues } from 'utils/data';

import { showMessageAction } from 'store/global/actions';
import { createCrmContactAction } from 'store/system/actions/crm.actions';

import {
  COMPANY_NAME_MAX_LENGTH,
  CONTACT_FORM_FIELD,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
} from './NewContactDrawer.constants';
import { NewContactDrawerTestIds } from './NewContactDrawer.testIds';
import { buildCompanySchema, buildPersonSchema } from './NewContactDrawer.utils';

const formFieldStyles = { marginBottom: '16px' };
const nameRowStyles = { display: 'flex', gap: '16px', marginBottom: '16px' };
const nameFieldStyles = { flex: 1 };
const contentStyles = { width: '100%', flexDirection: 'column' };

const initialForm = {
  [CONTACT_FORM_FIELD.FIRST_NAME]: '',
  [CONTACT_FORM_FIELD.LAST_NAME]: '',
  [CONTACT_FORM_FIELD.COMPANY_NAME]: '',
  [CONTACT_FORM_FIELD.PRIMARY_EMAIL]: '',
  [CONTACT_FORM_FIELD.PRIMARY_PHONE]: '',
  [CONTACT_FORM_FIELD.LIFECYCLE_STAGE]: LIFECYCLE_STAGE_OPTIONS[0],
  [CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD]: null,
  [CONTACT_FORM_FIELD.TYPE]: ContactType.PERSON,
};

const NewContactDrawer = ({ open, onClose, onContactCreated }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { form, formRef, handleChange, setSchema, validateForm, validationErrors, resetForm } =
    useForm(initialForm);

  const isPerson = form[CONTACT_FORM_FIELD.TYPE] === ContactType.PERSON;

  const isFormChanged = useMemo(() => {
    const trimmed = trimBodyStringValues(form);
    const trimmedInitial = trimBodyStringValues(initialForm);

    return (
      trimmed[CONTACT_FORM_FIELD.FIRST_NAME] !== trimmedInitial[CONTACT_FORM_FIELD.FIRST_NAME] ||
      trimmed[CONTACT_FORM_FIELD.LAST_NAME] !== trimmedInitial[CONTACT_FORM_FIELD.LAST_NAME] ||
      trimmed[CONTACT_FORM_FIELD.COMPANY_NAME] !==
        trimmedInitial[CONTACT_FORM_FIELD.COMPANY_NAME] ||
      trimmed[CONTACT_FORM_FIELD.PRIMARY_EMAIL] !==
        trimmedInitial[CONTACT_FORM_FIELD.PRIMARY_EMAIL] ||
      trimmed[CONTACT_FORM_FIELD.PRIMARY_PHONE] !==
        trimmedInitial[CONTACT_FORM_FIELD.PRIMARY_PHONE] ||
      form[CONTACT_FORM_FIELD.LIFECYCLE_STAGE]?.value !==
        initialForm[CONTACT_FORM_FIELD.LIFECYCLE_STAGE]?.value ||
      form[CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD]?.value !==
        initialForm[CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD]?.value ||
      form[CONTACT_FORM_FIELD.TYPE] !== initialForm[CONTACT_FORM_FIELD.TYPE]
    );
  }, [form]);

  const onLifecycleStageChange = useCallback(
    (_event, newValue) => {
      handleChange({
        target: {
          name: CONTACT_FORM_FIELD.LIFECYCLE_STAGE,
          value: newValue,
        },
      });
    },
    [handleChange],
  );

  const onPreferredContactMethodChange = useCallback(
    (_event, newValue) => {
      handleChange({
        target: {
          name: CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD,
          value: newValue,
        },
      });
    },
    [handleChange],
  );

  const onTypeChange = useCallback(
    e => {
      handleChange(e);
      handleChange({ target: { name: CONTACT_FORM_FIELD.FIRST_NAME, value: '' } });
      handleChange({ target: { name: CONTACT_FORM_FIELD.LAST_NAME, value: '' } });
      handleChange({ target: { name: CONTACT_FORM_FIELD.COMPANY_NAME, value: '' } });
    },
    [handleChange],
  );

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const onSubmit = useCallback(async () => {
    if (!(await validateForm({ noFormChangedValidate: true }))) {
      return;
    }

    setIsLoading(true);

    const currentForm = formRef.current;
    const currentType = currentForm[CONTACT_FORM_FIELD.TYPE] || ContactType.PERSON;
    const isPersonType = currentType === ContactType.PERSON;

    const preferredContactMethodValue =
      currentForm[CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD]?.value || null;

    const payload = isPersonType
      ? {
          firstName: currentForm[CONTACT_FORM_FIELD.FIRST_NAME]?.trim(),
          lastName: currentForm[CONTACT_FORM_FIELD.LAST_NAME]?.trim(),
          primaryEmail: currentForm[CONTACT_FORM_FIELD.PRIMARY_EMAIL]?.trim() || null,
          primaryPhone: currentForm[CONTACT_FORM_FIELD.PRIMARY_PHONE]?.trim() || null,
          lifecycleStage: currentForm[CONTACT_FORM_FIELD.LIFECYCLE_STAGE]?.value || 'Lead',
          ...getConditionalObject(preferredContactMethodValue, () => ({
            preferredContactMethod: preferredContactMethodValue,
          })),
          type: ContactType.PERSON,
        }
      : {
          firstName: currentForm[CONTACT_FORM_FIELD.COMPANY_NAME]?.trim(),
          lastName: '',
          primaryEmail: currentForm[CONTACT_FORM_FIELD.PRIMARY_EMAIL]?.trim() || null,
          primaryPhone: currentForm[CONTACT_FORM_FIELD.PRIMARY_PHONE]?.trim() || null,
          lifecycleStage: currentForm[CONTACT_FORM_FIELD.LIFECYCLE_STAGE]?.value || 'Lead',
          ...getConditionalObject(preferredContactMethodValue, () => ({
            preferredContactMethod: preferredContactMethodValue,
          })),
          type: ContactType.COMPANY,
        };

    dispatch(createCrmContactAction(payload))
      .then(res => {
        const displayName = isPersonType
          ? `${payload.firstName} ${payload.lastName}`
          : payload.firstName;

        dispatch(
          showMessageAction({
            message: `Contact ${displayName} created successfully`,
            variant: 'success',
          }),
        );
        handleClose();
        onContactCreated?.(res?.payload);
      })
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, validateForm, formRef, handleClose, onContactCreated]);

  useEffect(() => {
    setSchema(isPerson ? buildPersonSchema() : buildCompanySchema());
  }, [setSchema, isPerson]);

  const nameRowVisibilityStyles = useMemo(
    () => ({ ...nameRowStyles, display: isPerson ? 'flex' : 'none' }),
    [isPerson],
  );

  const companyNameRowStyles = useMemo(
    () => ({ ...formFieldStyles, display: isPerson ? 'none' : 'block' }),
    [isPerson],
  );

  const drawerContent = useMemo(
    () => (
      <Box sx={contentStyles}>
        <Box sx={formFieldStyles}>
          <RadioGroupCDS
            row
            dataTestId={NewContactDrawerTestIds.TYPE_RADIO}
            label="Type"
            name={CONTACT_FORM_FIELD.TYPE}
            options={CONTACT_TYPE_OPTIONS}
            value={form[CONTACT_FORM_FIELD.TYPE]}
            onChange={onTypeChange}
          />
        </Box>

        <Box sx={nameRowVisibilityStyles}>
          <Box sx={nameFieldStyles}>
            <TextFieldCDS
              required
              dataTestId={NewContactDrawerTestIds.FIRST_NAME_INPUT}
              error={!!validationErrors[CONTACT_FORM_FIELD.FIRST_NAME]}
              helperText={validationErrors[CONTACT_FORM_FIELD.FIRST_NAME]}
              label="First Name"
              maxLength={FIRST_NAME_MAX_LENGTH}
              name={CONTACT_FORM_FIELD.FIRST_NAME}
              value={form[CONTACT_FORM_FIELD.FIRST_NAME]}
              width="100%"
              onChange={handleChange}
            />
          </Box>
          <Box sx={nameFieldStyles}>
            <TextFieldCDS
              required
              dataTestId={NewContactDrawerTestIds.LAST_NAME_INPUT}
              error={!!validationErrors[CONTACT_FORM_FIELD.LAST_NAME]}
              helperText={validationErrors[CONTACT_FORM_FIELD.LAST_NAME]}
              label="Last Name"
              maxLength={LAST_NAME_MAX_LENGTH}
              name={CONTACT_FORM_FIELD.LAST_NAME}
              value={form[CONTACT_FORM_FIELD.LAST_NAME]}
              width="100%"
              onChange={handleChange}
            />
          </Box>
        </Box>

        <Box sx={companyNameRowStyles}>
          <TextFieldCDS
            required
            dataTestId={NewContactDrawerTestIds.COMPANY_NAME_INPUT}
            error={!!validationErrors[CONTACT_FORM_FIELD.COMPANY_NAME]}
            helperText={validationErrors[CONTACT_FORM_FIELD.COMPANY_NAME]}
            label="Company Name"
            maxLength={COMPANY_NAME_MAX_LENGTH}
            name={CONTACT_FORM_FIELD.COMPANY_NAME}
            value={form[CONTACT_FORM_FIELD.COMPANY_NAME]}
            width="100%"
            onChange={handleChange}
          />
        </Box>

        <Box sx={formFieldStyles}>
          <TextFieldCDS
            dataTestId={NewContactDrawerTestIds.EMAIL_INPUT}
            error={!!validationErrors[CONTACT_FORM_FIELD.PRIMARY_EMAIL]}
            helperText={validationErrors[CONTACT_FORM_FIELD.PRIMARY_EMAIL]}
            label="Email"
            name={CONTACT_FORM_FIELD.PRIMARY_EMAIL}
            value={form[CONTACT_FORM_FIELD.PRIMARY_EMAIL]}
            width="100%"
            onChange={handleChange}
          />
        </Box>

        <Box sx={formFieldStyles}>
          <DataFieldCDS
            dataTestId={NewContactDrawerTestIds.PHONE_INPUT}
            error={!!validationErrors[CONTACT_FORM_FIELD.PRIMARY_PHONE]}
            helperText={validationErrors[CONTACT_FORM_FIELD.PRIMARY_PHONE]}
            label="Phone"
            name={CONTACT_FORM_FIELD.PRIMARY_PHONE}
            type={FieldInputType.TEL_EXT}
            value={form[CONTACT_FORM_FIELD.PRIMARY_PHONE]}
            width="100%"
            onChange={handleChange}
          />
        </Box>

        <Box sx={formFieldStyles}>
          <AutocompleteCDS
            disableClearable
            dataTestId={NewContactDrawerTestIds.LIFECYCLE_STAGE_SELECT}
            label="Lifecycle Stage"
            name={CONTACT_FORM_FIELD.LIFECYCLE_STAGE}
            options={LIFECYCLE_STAGE_OPTIONS}
            value={form[CONTACT_FORM_FIELD.LIFECYCLE_STAGE]}
            width="100%"
            onChange={onLifecycleStageChange}
          />
        </Box>

        <Box sx={formFieldStyles}>
          <AutocompleteCDS
            dataTestId={NewContactDrawerTestIds.PREFERRED_CONTACT_METHOD_SELECT}
            label="Preferred Contact Method"
            name={CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD}
            options={PREFERRED_CONTACT_METHOD_OPTIONS}
            value={form[CONTACT_FORM_FIELD.PREFERRED_CONTACT_METHOD]}
            width="100%"
            onChange={onPreferredContactMethodChange}
          />
        </Box>
      </Box>
    ),
    [
      form,
      validationErrors,
      handleChange,
      onLifecycleStageChange,
      onPreferredContactMethodChange,
      onTypeChange,
      nameRowVisibilityStyles,
      companyNameRowStyles,
    ],
  );

  const drawerActions = useMemo(
    () => (
      <Button
        data-testid={NewContactDrawerTestIds.SUBMIT_BUTTON}
        disabled={isLoading || !isFormChanged}
        onClick={onSubmit}
      >
        Create Contact
      </Button>
    ),
    [isLoading, isFormChanged, onSubmit],
  );

  return (
    <AdminSlideDrawer
      actions={drawerActions}
      content={drawerContent}
      isLoading={isLoading}
      open={open}
      title="New Contact"
      onClose={handleClose}
    />
  );
};

NewContactDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContactCreated: PropTypes.func,
};

export default NewContactDrawer;
